// Task utilities for Related Tasks functionality
import { TaskStatus } from '../types';
import type { Task, TaskListItem } from '../types';
import type { TFunction } from 'i18next';

/**
 * Detects circular dependencies in task graph using DFS algorithm
 *
 * @param taskId - ID of the task being checked
 * @param newRelatedTaskIds - New related task IDs to validate
 * @param allTasks - All tasks in the system
 * @returns true if circular dependency detected, false otherwise
 *
 * @example
 * // Task A -> Task B -> Task C -> Task A (circular)
 * detectCircularDependency('A', ['B'], allTasks) // true if B->C->A exists
 */
export function detectCircularDependency(
  taskId: string,
  newRelatedTaskIds: string[],
  allTasks: ReadonlyArray<Task | TaskListItem>
): boolean {
  const visited = new Set<string>();
  const recursionStack = new Set<string>();

  function hasCycle(currentId: string): boolean {
    // If we're back in the recursion stack, we found a cycle
    if (recursionStack.has(currentId)) return true;

    // If already visited (but not in recursion stack), no cycle from this node
    if (visited.has(currentId)) return false;

    // Mark as visited and add to recursion stack
    visited.add(currentId);
    recursionStack.add(currentId);

    // Find current task
    const foundTask = allTasks.find((listTask) => listTask.id === currentId);
    if (!foundTask) {
      // Task not found, remove from recursion stack and continue
      recursionStack.delete(currentId);
      return false;
    }

    // Get related task IDs (use new ones if this is the task being modified)
    // TaskListItem doesn't have relatedTasks — skip traversal in that case
    const relatedIds =
      currentId === taskId
        ? newRelatedTaskIds
        : 'relatedTasks' in foundTask
          ? foundTask.relatedTasks.map((relatedTask) => relatedTask.id)
          : [];

    // Check all related tasks for cycles
    for (const relatedId of relatedIds) {
      if (hasCycle(relatedId)) {
        return true;
      }
    }

    // Remove from recursion stack (backtrack)
    recursionStack.delete(currentId);
    return false;
  }

  return hasCycle(taskId);
}

/**
 * Checks if a task can be automatically completed
 *
 * Conditions for auto-completion:
 * 1. All requirements must be completed
 * 2. If requireRelatedTasksCompletion is true, all related tasks must be completed
 *
 * @param task - Task to check
 * @returns true if task can be auto-completed, false otherwise
 *
 * @example
 * const task = { requirements: [...], requireRelatedTasksCompletion: true, ... };
 * canAutoCompleteTask(task) // true/false
 */
export function canAutoCompleteTask(task: Task): boolean {
  // 1. Check if all requirements are completed
  const allRequirementsCompleted = task.requirements.every(
    (req) => req.completed
  );
  if (!allRequirementsCompleted) return false;

  // 2. If requireRelatedTasksCompletion is true, check related tasks
  if (task.requireRelatedTasksCompletion && task.relatedTasks.length > 0) {
    const allRelatedCompleted = task.relatedTasks.every(
      (relatedTask) => relatedTask.status === TaskStatus.Completed
    );
    return allRelatedCompleted;
  }

  // All conditions met
  return true;
}

/**
 * Gets progress statistics for related tasks
 *
 * @param task - Task to get related tasks progress for
 * @returns Progress object with completed, total, and percentage
 *
 * @example
 * const progress = getRelatedTasksProgress(task);
 * console.log(progress); // { completed: 3, total: 5, percentage: 60 }
 */
export function getRelatedTasksProgress(task: Task): {
  completed: number;
  total: number;
  percentage: number;
} {
  const total = task.relatedTasks.length;
  const completed = task.relatedTasks.filter(
    (relatedTask) => relatedTask.status === TaskStatus.Completed
  ).length;
  const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;

  return { completed, total, percentage };
}

/**
 * Whether a task has any comments worth surfacing — either inlined comment
 * objects or a server-provided count. Drives the comments tab/section
 * visibility for both the authenticated and public task detail views.
 *
 * @param task - Task-like object exposing `comments` and/or `commentCount`
 * @returns true if at least one comment exists
 */
export function taskHasComments(task: {
  comments?: readonly unknown[] | null;
  commentCount?: number | null;
}): boolean {
  return (task.comments?.length ?? 0) > 0 || (task.commentCount ?? 0) > 0;
}

/**
 * Formats task status for display with internationalization
 *
 * @param status - TaskStatus enum value
 * @param t - i18next translation function
 * @returns Localized status string
 *
 * @example
 * formatTaskStatus(TaskStatus.InProgress, t) // "W trakcie" (PL) / "In Progress" (EN)
 */
export function formatTaskStatus(status: TaskStatus, t: TFunction): string {
  switch (status) {
    case TaskStatus.Pending:
      return t('common.pending');
    case TaskStatus.InProgress:
      return t('common.inProgress');
    case TaskStatus.Delayed:
      return t('common.delayed');
    case TaskStatus.Completed:
      return t('common.completed');
    case TaskStatus.Archived:
      return t('taskStatus.archived');
    default:
      // Fallback for unknown statuses
      return String(status).replace('_', ' ').toUpperCase();
  }
}
